import { ImgHTMLAttributes, ForwardedRef, CSSProperties } from 'react';

import useFormatImageUrl from '../../hooks/useFormatImageUrl';
import getImageSize from '../../lib/getImageSize';
import getOptimalImageSize from '../../lib/getOptimalImageSize';

export interface PictureProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'ref'> {
    media?: Image | null;
    width?: number | string | null;
    height?: number | string | null;
    size?: string | null;
    resolution?: number;
    fetchPriority?: 'high' | 'low' | 'auto' | null;
    loading?: 'lazy' | null;
    className?: string | null;
    style?: CSSProperties | null;
}

function Picture({
    media = null,
    width = null,
    height = null,
    size = null,
    resolution = 2,
    fetchPriority = null,
    alt = null,
    loading = null,
    className = null,
    style = null,
    ref: imgRef = null,
    ...props
}: PictureProps & { ref?: ForwardedRef<HTMLImageElement> | null }) {
    const formatImageUrl = useFormatImageUrl();
    const { sizes = null, description = null } = media || {};
    const {
        url: imageUrl,
        width: imageWidth,
        height: imageHeight,
    } = getImageSize(media, size) ||
    getOptimalImageSize(media, width, height, {
        resolution,
    }) ||
    {};

    const sortedSizes = (sizes || [])
        .sort(({ width: widthA }, { width: widthB }) => (widthA > widthB ? 1 : -1))
        .filter(({ id }) => id !== 'original');

    return (
        <img
            {...props}
            src={formatImageUrl(imageUrl)}
            srcSet={
                size === 'responsive'
                    ? sortedSizes
                          .map(
                              ({ url: sizeUrl, width: sizeWidth }) =>
                                  `${formatImageUrl(sizeUrl)} ${sizeWidth}w`,
                          )
                          .join(', ')
                    : undefined
            }
            sizes={size === 'responsive' ? '100vw' : undefined}
            alt={alt || description || undefined}
            width={imageWidth}
            height={imageHeight}
            loading={loading || undefined}
            className={className || undefined}
            fetchPriority={fetchPriority || undefined}
            ref={imgRef}
            style={style}
        />
    );
}

export default Picture;
